import page from "page";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { runOperation } from "../../ui/operation";
import el from "@cypherpotato/el";
import { api } from "../../api";
import { Card, CardContent, CardHeader } from "../../components/card";
import { formatCurrency, formatDate, formatFlexibibleFloat, formatFloat, formatNumber, formatTokenCount } from "../../ui/format";
import { getProviderColor } from "../../ui/provider-colors";
import { cond, find, pipe, repeatNode } from "../../util";
import { Icon } from "../../components/icon";
import { CopiableText, MultilingualCodeWrapper, MutedText } from "../../components/text";
import { Badge, BadgeList, IconBadge, TagBadge, TagIconBadge } from "../../components/badge";
import { FormControl, MultiSelect, Select, TextInput } from "../../components/form";
import { throttledCall } from "../../ui/throttle";
import { CommandBar } from "../../components/command-bar";
import { Content, Section } from "../../components/layout";
import { Dropdown, DropdownDivider, DropdownItem } from "../../components/dropdown";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { SpecializedControl } from "../../components/specializedObject";
import { getSession } from "../../session";
import { copyToClipboard } from "../../ui/clipboard";
import { getUniqueColor } from "../../ui/unique-color";
import { getModelPricingTiers, matchesOutputPriceFilter } from "../../ui/model-pricing";
import { TabControl } from "../../components/tabcontrol";
import { Button } from "../../components/button";

const ModelPageSize = 15;

export default function (ctx) {

    const imageModels = el.state.create(null);
    const transcriptionModels = el.state.create(null);
    const speechModels = el.state.create(null);
    const rerankingModels = el.state.create(null);
    const decisionModels = el.state.create(null);
    const models = el.state.create(null);
    const visibleModelCount = el.state.create(ModelPageSize);

    const focusedModel = ctx.query?.focus ?? null;
    const filterName = el.state.create(focusedModel);
    const filterPricing = el.state.create(null);
    const filterSpeed = el.state.create(null);
    const filterIntelligence = el.state.create(null);
    const filterSubscription = el.state.create(null);
    const filterReleaseDate = el.state.create(null);
    const filterCapability = el.state.create([]);

    runOperation("Loading models...", async () => {
        const response = await api.getJson("/api/v1/information/models.json");
        models.value = flatMapModels(response.data);
    });

    const loadCatalog = async (state, endpoint) => {
        state.value = null;
        try {
            const response = await api.getJson(`/api/v1/information/${endpoint}`);
            state.value = {
                models: response.data
                    .filter(model => model.name !== "none")
                    .map(model => ({ ...model, slug: model.name.includes("/") ? model.name : `@aivax/${model.name}` }))
            };
        } catch {
            state.value = { error: true };
        }
    };

    loadCatalog(imageModels, "image-models.json");
    loadCatalog(transcriptionModels, "audio-transcription-models.json");
    loadCatalog(speechModels, "speech-models.json");
    loadCatalog(rerankingModels, "rerankers-models.json");
    loadCatalog(decisionModels, "decisions-models.json");

    const newestFirst = (a, b) =>
        (Date.parse(b.releaseDate ?? b.modelInformation?.releaseDate) || 0)
        - (Date.parse(a.releaseDate ?? a.modelInformation?.releaseDate) || 0)
        || a.name.localeCompare(b.name);

    function flatMapModels(res) {
        return res.flatMap(provider => provider.models).sort(newestFirst);
    }

    const CatalogList = (state, title, endpoint) => {
        const filter = el.state.create("");

        return Content(
            Section(
                CommandBar(
                    FormControl("Search models",
                        TextInput(`catalog-search-${endpoint}`, "Search for model", {
                            type: "search",
                            onInput(event) {
                                throttledCall(() => {
                                    filter.value = event.target.value.trim();
                                }, 300);
                            }
                        })
                    )
                )
            ),
            el.state.listening([state, filter], () =>
                Section(
                    Card({ classList: "bg-card" },
                        CardHeader(title, state.value?.models && `${state.value.models.length} models`),
                        CardContent(
                            el(".model-list-view",
                                cond(
                                    [state.value === null, () => el(".no-models", "Loading models...")],
                                    [state.value?.error, () => el(".no-models",
                                        "Could not load models. ",
                                        Button("Retry", { onClick: () => loadCatalog(state, endpoint) })
                                    )],
                                    [true, () => state.value.models
                                        .filter(model => filter.value.split(" ").every(part =>
                                            (model.slug + (model.displayName ?? "")).replace(/[^a-zA-Z0-9]/g, "").toLowerCase()
                                                .includes(part.replace(/[^a-zA-Z0-9]/g, "").toLowerCase())
                                        ))
                                        .toSorted(newestFirst)
                                        .map(model =>
                                            el(".model-item",
                                                { class: [model.flags?.includes("Deprecated") && "deprecated"] },
                                                ModelBanner(model.slug, endpoint == "image-models.json" && `/assets/imggenmodels/${model.slug.replace(/^@.*\//, "")}.webp`),
                                                el(".model-data",
                                                    el("h2",
                                                        modelProviderIcon(model.slug),
                                                        el(".model-details-link",
                                                            model.name.includes("/") && MutedText(model.name.replace(/\/.*/, '') + "/"),
                                                            model.name.replace(/^@.*\//, ''),
                                                            Icon("file-copy-line"),
                                                            {
                                                                type: "button",
                                                                title: `Copy ${model.name}`,
                                                                onClick: () => copyToClipboard(model.name)
                                                            }
                                                        )
                                                    ),
                                                    BadgeList(
                                                        model.isDefault && Badge(el.fragment(Icon("checkbox-circle-line"), "Default"), "#0f8b8d", {
                                                            title: "This model is used when no model is specified."
                                                        }),
                                                        model.subscriptionUsage && Badge("Subscription included", "rgb(25, 81, 235)", {
                                                            title: "Eligible for the plan's daily service allowance, subject to available capacity."
                                                        }),
                                                        model.autonomousUse === false && Badge(el.fragment(Icon("git-merge-line"), "RAG only"), "#4a5d7e", {
                                                            title: "This reranker can only be used after RAG retrieval."
                                                        }),
                                                        model.flags?.includes("Deprecated") && Badge(el.fragment(Icon("delete-bin-7-line"), "Deprecated"), "#c9662dff", {
                                                            title: "This model is deprecated and will be removed in the future."
                                                        }),
                                                        {
                                                            init() {
                                                                if (this.children.length === 0) {
                                                                    this.style.display = "none";
                                                                }
                                                            }
                                                        }
                                                    ),
                                                    el("p", model.description || MutedText("Description not published.")),
                                                    el(".model-info",
                                                        el(".model-info-item.clickable",
                                                            "by ", el("strong", modelProvider(model.slug), { style: { fontWeight: 500 } }),
                                                            {
                                                                onClick() {
                                                                    find(`catalog-search-${endpoint}`).value = '@' + modelProvider(model.slug);
                                                                    filter.value = '@' + modelProvider(model.slug);
                                                                    scrollTo(0, 0);
                                                                }
                                                            }
                                                        ),
                                                        model.releaseDate && el(".model-info-item",
                                                            "released ", formatDate(model.releaseDate)
                                                        ),
                                                        model.aliases?.length > 0 && el(".model-info-item",
                                                            Icon("corner-up-right-double-fill"), " ", model.aliases.join(", "),
                                                            {
                                                                title: "Also available as " + model.aliases.join(", ")
                                                            }
                                                        )
                                                    ),
                                                    el(".model-info-item-table",
                                                        el(".model-info-item-row",
                                                            model.pricingDescription?.includes("$")
                                                                ? model.pricingDescription.split(/\n|\s\+\s/).filter(part => part.includes("$")).map(part =>
                                                                    pipe(part.match(/\$([\d.,]+)\s*(.*)/) ?? [part, "0", part], ([, price, unit]) =>
                                                                        pipe(cond(
                                                                            [unit.includes("reference image"), ["image-add-line", "Reference", "/image"]],
                                                                            [unit.includes("output image"), ["image-line", "Output", "/image"]],
                                                                            [unit.includes("1,000 characters"), ["text", "Input", "/1K chars"]],
                                                                            [unit.includes("minute"), ["volume-up-line", "Audio", "/min"]],
                                                                            [unit.includes("cache hit"), ["arrow-down-double-line", "Cached", "/mtok"]],
                                                                            [unit.includes("output tokens"), ["arrow-up-s-line", "Output", "/mtok"]],
                                                                            [unit.includes("tokens"), ["arrow-down-s-line", "Input", "/mtok"]],
                                                                            [unit.includes("search unit"), ["search-line", "Search", "/unit"]],
                                                                            [true, ["price-tag-3-line", "Price", unit]]
                                                                        ), ([icon, label, suffix]) =>
                                                                            el(".model-info-item-col",
                                                                                {
                                                                                    title: part.trim(),
                                                                                    style: {
                                                                                        "--col-width": "80px"
                                                                                    }
                                                                                },
                                                                                el(".definition",
                                                                                    el("span", Icon(icon), " ", label),
                                                                                    el("span", Number(price.replace(/,/g, "")).toLocaleString("en-US", {
                                                                                        style: "currency",
                                                                                        currency: "USD",
                                                                                        minimumFractionDigits: 2,
                                                                                        maximumFractionDigits: 7
                                                                                    }))
                                                                                ),
                                                                                el(".definition", { style: { opacity: .65 } },
                                                                                    el("span"),
                                                                                    el("span", suffix, model.pricingDescription.includes("estimate") && " (est.)")
                                                                                )
                                                                            )
                                                                        )
                                                                    )
                                                                )
                                                                : el(".model-info-item-col",
                                                                    {
                                                                        style: {
                                                                            "--col-width": "80px"
                                                                        }
                                                                    },
                                                                    el(".definition",
                                                                        el("span", Icon("price-tag-3-line"), " Price"),
                                                                        el("span", model.pricingDescription || "Not published")
                                                                    )
                                                                )
                                                        )
                                                    )
                                                ),
                                                el(".model-meta",
                                                    (model.contextLength ?? model.technicalInformation?.contextSize) > 0 && el(".model-meta-item",
                                                        el("strong", "Context"),
                                                        el(".model-meta-item-number-label", ShortSize(model.contextLength ?? model.technicalInformation.contextSize)),
                                                        {
                                                            title: `${formatNumber(model.contextLength ?? model.technicalInformation.contextSize)} tokens`
                                                        }
                                                    ),
                                                    model.technicalInformation?.maxDocuments > 0 && el(".model-meta-item",
                                                        el("strong", "Max documents"),
                                                        el(".model-meta-item-number-label", ShortSize(model.technicalInformation.maxDocuments)),
                                                        {
                                                            title: `Up to ${formatNumber(model.technicalInformation.maxDocuments)} documents per request`
                                                        }
                                                    ),
                                                    model.maxImages > 0 && el(".model-meta-item",
                                                        el("strong", "Max images"),
                                                        el(".model-meta-item-number-label", formatNumber(model.maxImages)),
                                                        {
                                                            title: `Up to ${formatNumber(model.maxImages)} images per request`
                                                        }
                                                    ),
                                                    model.supportedFormats?.length > 0 && el(".model-meta-item",
                                                        el("strong", "Formats"),
                                                        el(".iconic-information", el("div", model.supportedFormats.join(", ")))
                                                    ),
                                                    [...(model.flags ?? []), ...(model.capabilities ?? [])].some(capability => capability !== "Deprecated") && el(".model-meta-item",
                                                        el("strong", "Capabilities"),
                                                        el(".iconic-information",
                                                            [...(model.flags ?? []), ...(model.capabilities ?? [])].map(capability =>
                                                                cond(
                                                                    [capability == "AllowReferenceImage", () => createCapability("image-edit-line", "Image editing", "The model accepts reference images", true)],
                                                                    [capability == "AllowMatureContent", () => createCapability("eye-off-line", "Mature content", "The model can generate mature content", true)],
                                                                    [capability == "noul", () => createCapability("checkbox-circle-line", "Yes/no", "The model answers yes or no questions", true)],
                                                                    [capability == "choice", () => createCapability("list-check", "Choice", "The model picks one of the given options", true)],
                                                                    [capability == "score", () => createCapability("bar-chart-line", "Score", "The model rates the input on a numeric scale", true)],
                                                                    [capability == "imageinput", () => createCapability("image-line", "Image input", "The model can understand images", true)]
                                                                )
                                                            )
                                                        )
                                                    )
                                                ),
                                                el(".model-actions",
                                                    Dropdown(null,
                                                        DropdownItem("Copy model name", Icon("file-copy-line"), {
                                                            onClick: () => copyToClipboard(model.name)
                                                        }),
                                                        (model.documentationUrl || model.sourceUrl) && DropdownDivider(),
                                                        model.documentationUrl && DropdownItem("Integration docs", Icon("book-open-line"), {
                                                            onClick: () => window.open(model.documentationUrl, "_blank", "noopener,noreferrer")
                                                        }),
                                                        model.sourceUrl && DropdownItem("Model source", Icon("external-link-line"), {
                                                            onClick: () => window.open(model.sourceUrl, "_blank", "noopener,noreferrer")
                                                        })
                                                    )
                                                )
                                            )
                                        ).nullWhenEmpty() || el(".no-models", "No models found matching your criteria.")
                                    ]
                                )
                            )
                        )
                    )
                )
            )
        );
    };

    function keyedMap(i) {
        // return random number between 01 and 39 based on the string
        let hash = 0;
        for (let j = 0; j < i.length; j++) {
            hash = i.charCodeAt(j) + ((hash << 5) - hash);
        }
        return (Math.abs(hash) % 38 + 1).toString().padStart(2, "0");
    }

    const ShortSize = size => cond(
        [size >= 1_000_000, (size / 1_000_000).toFixed(0) + "M"],
        [size >= 1_000, (size / 1_000).toFixed(0) + "K"],
        [true, formatNumber(size)]
    )

    const FilterCallback = model => {
        return FilterNameCallback(model)
            && FilterPricingCallback(model)
            && FilterSpeedCallback(model)
            && FilterIntelligenceCallback(model)
            && FilterSubscriptionIncluded(model)
            && FilterReleaseDateCallback(model)
            && FilterCapabilityCallback(model);
    }

    const resetVisibleModels = () => {
        visibleModelCount.value = ModelPageSize;
    }

    const filteredModels = () => models.value?.filter(FilterCallback) ?? [];

    const loadMoreModels = total => {
        if (visibleModelCount.value < total) {
            visibleModelCount.value = Math.min(visibleModelCount.value + ModelPageSize, total);
        }
    }

    const LoadMoreSentinel = total => el(".model-list-load-more", {
        mount() {
            if (visibleModelCount.value >= total) {
                return;
            }

            const observer = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    observer.disconnect();
                    loadMoreModels(total);
                }
            }, {
                rootMargin: "240px"
            });

            observer.observe(this);
        }
    });

    const FilterNameCallback = model => {
        if (!filterName.value) {
            return true;
        }

        const onlyCharsModelName = model.name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        for (const part of filterName.value.split(" ")) {
            const onlyCharsFilter = part.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

            if (!onlyCharsModelName.includes(onlyCharsFilter)) {
                return false;
            }
        }

        return true;
    }

    const FilterPricingCallback = model => {
        if (!filterPricing.value) {
            return true;
        }

        return (model.providers ?? []).some(provider => matchesOutputPriceFilter(provider, filterPricing.value));
    }

    const enumValue = value => Array.isArray(value) ? value[0] : value;
    const providerGroupOrder = ["Default", "Flex", "Priority", "Special"];

    const FilterSpeedCallback = model => {
        if (!filterSpeed.value) {
            return true;
        }

        return enumValue(model.speedLevel) == filterSpeed.value;
    }

    const FilterIntelligenceCallback = model => {
        if (!filterIntelligence.value) {
            return true;
        }

        return enumValue(model.intelligenceLevel) == filterIntelligence.value;
    }

    const FilterSubscriptionIncluded = model => {
        if (!filterSubscription.value) {
            return true;
        }

        return model.pricing?.at(0)?.subscriptionMultiplier != null;
    }

    const FilterReleaseDateCallback = model => {
        if (!filterReleaseDate.value) {
            return true;
        }

        const releaseDate = model.modelInformation?.releaseDate;
        if (!releaseDate) {
            return false;
        }

        const released = new Date(releaseDate);
        const now = new Date();
        let cutoff;

        switch (filterReleaseDate.value) {
            case "thisweek":
                cutoff = new Date(now);
                cutoff.setDate(now.getDate() - (now.getDay() + 6) % 7);
                cutoff.setHours(0, 0, 0, 0);
                break;
            case "thismonth":
                cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
                break;
            case "threemonths":
                cutoff = new Date(now);
                cutoff.setMonth(now.getMonth() - 3);
                break;
            case "thisyear":
                cutoff = new Date(now.getFullYear(), 0, 1);
                break;
            default:
                return true;
        }

        return released >= cutoff;
    }

    const FilterCapabilityCallback = model => {
        const selected = filterCapability.value.nullWhenEmpty();
        if (!selected || selected.includes("")) {
            return true;
        }

        return selected.some(capability => (model.capabilities ?? []).includes(capability));
    }

    function fitBannerName(element) {
        const maxFontSizePx = 40;
        const minFontSizePx = 10;
        const widthSafetyPx = 4;

        element.style.fontSize = `${maxFontSizePx}px`;

        const availableWidth = Math.max(0, element.clientWidth - widthSafetyPx);

        if (!availableWidth) {
            return;
        }

        let nextFontSize = maxFontSizePx;

        for (let i = 0; i < 6; i++) {
            const requiredWidth = element.scrollWidth;

            if (!requiredWidth || requiredWidth <= availableWidth) {
                break;
            }

            nextFontSize = Math.max(minFontSizePx, nextFontSize * availableWidth / requiredWidth);
            element.style.fontSize = `${nextFontSize.toFixed(2)}px`;
        }

        if (element.scrollWidth > availableWidth) {
            const adjustedFontSize = Math.max(minFontSizePx, nextFontSize - 1);
            element.style.fontSize = `${adjustedFontSize.toFixed(2)}px`;
        }
    }

    // const createCapability = (icon, text, tooltip, includeLabel = false) => el(".model-capability",
    //     Icon(icon, { style: { backgroundColor: getUniqueColor(text) } }), includeLabel && el("span", text), { title: `${text}: ${tooltip}` });
    const createCapability = (icon, text, tooltip, includeLabel = false) =>
        el(".model-capability",
            Icon(icon, { style: { backgroundColor: getUniqueColor(text) } }), includeLabel && el("span", text), { title: `${text}: ${tooltip}` }
        );

    const ModelBanner = (modelName, preview) => preview ? el(".model-banner.preview",
        el("img", {
            src: preview,
            alt: `Image generated by ${modelName}`,
            loading: "lazy"
        })
    ) : el(".model-banner",
        el("img", {
            src: `/assets/meshes/abstract_gradient_${keyedMap(modelName)}.png`,
            alt: ""
        }),
        el("div",
            {
                style: {
                    backgroundColor: getUniqueColor(modelName),
                    "--provider-tint": getProviderColor(modelName)
                }
            }
        ),
        el("span",
            el("div.provider",
                modelName.replace(/\/.*/, '').replace(/^@/, '')
            ),
            el("div.name",
                modelName.replace(/^@.*\//, ''),
                {
                    mount() {
                        const fit = () => fitBannerName(this);

                        new ResizeObserver((_, observer) => {
                            if (this.clientWidth) {
                                fit();
                                observer.disconnect();
                            }
                        }).observe(this);
                        document.fonts?.ready?.then(() => requestAnimationFrame(fit));
                    }
                }
            )
        )
    );

    const modelProvider = modelName => modelName.replace(/\/.*/, '').replace(/^@/, '');
    const modelProviderIcon = (modelName, providerName = null) => el(".model-provider-icon-wrapper",
        {
            style: {
                "--provider-color-alt": getProviderColor(providerName ?? modelName) + "66",
                "--provider-color": getProviderColor(providerName ?? modelName),
            }
        },
        el("img.model-provider-icon", {
            src: providerName
                ? `/assets/providers/${providerName.replace(/:offline$/, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}.svg`
                : `/assets/providers/${modelProvider(modelName)}.svg`,
            alt: "",
            onError() {
                console.error("Missing provider icon for", providerName ?? modelName);
                this.src = "/assets/providers/unknown.svg";
            }
        })
    );

    const renderModelBadges = model => {
        const providers = (model.providers ?? []).filter(provider => !provider.isDisabled);
        const hasProviderGroup = group => providers.some(provider => enumValue(provider.group) == group);

        return BadgeList(
            model.pricing?.at(0)?.subscriptionMultiplier != null && Badge("Subscription included", "rgb(25, 81, 235)", {
                title: "This model has included usage for AIVAX subscription plans."
            }),
            model.flags.isNew && Badge(el.fragment(Icon("star-s-line"), "New"), "#6c13c0ff", {
                title: "This model was recently added."
            }),
            model.flags.isPreview && Badge(el.fragment(Icon("flask-line"), "Preview"), "#8fc92d", {
                title: "This model is in preview and should be used with caution, as it may be removed or changed in the future."
            }),
            model.flags.isDeprecating && Badge(el.fragment(Icon("delete-bin-7-line"), "Deprecated"), "#c9662dff", {
                title: "This model is deprecated and will be removed in the future."
            }),
            model.flags.minimumTier > 0 && Badge(el.fragment(Icon("error-warning-line"), `Minimum tier: ${model.flags.minimumTier}`), "#c92d69ff", {
                title: `The minimum tier to use this model is tier ${model.flags.minimumTier}.`
            }),
            model.isDiscounted && Badge(el.fragment(Icon("discount-percent-line"), "Discounted"), "rgb(8, 134, 75)", {
                title: "This model is currently offered at a discounted rate for a limited time."
            }),
            model.flags.isFree && Badge(el.fragment(Icon("service-line"), "Free"), "#ff69f3", {
                title: "This model is offered for free, with no usage charges. Data may be used for training."
            }),
            hasProviderGroup("Flex") && Badge(el.fragment(Icon("hourglass-line"), "Flex"), "#0f8b8d", {
                title: "This model has non-priority (Flex) providers available. They are not included on standard routing."
            }),
            hasProviderGroup("Priority") && Badge(el.fragment(Icon("rocket-line"), "Priority"), "#d97706", {
                title: "This model has Priority providers available. They are not included on standard routing."
            }),
            providers.some(provider => enumValue(provider.technicalInfo?.dataCollection) == "Zero") && Badge(el.fragment(Icon("shield-line"), "ZDR"), "#4a5d7e", {
                title: "This model has providers with zero data retention available."
            }),
            hasProviderGroup("Special") && Badge(el.fragment(Icon("sparkling-line"), "Special"), "#a4508b", {
                title: "This model has special providers available, offered through non-standard deals. They are only used when explicitly allowed."
            }),
            {
                init() {
                    if (this.children.length === 0) {
                        this.style.display = "none";
                    }
                }
            }
        );
    };

    const renderModelCapabilities = (model, includeLabels = false) => model.capabilities?.map(capability =>
        cond(
            [capability == "ImageInput", createCapability("image-line", "Image input", "The model can understand images", includeLabels)],
            [capability == "AudioInput", createCapability("volume-up-line", "Audio input", "The model can understand audio", includeLabels)],
            [capability == "VideoInput", createCapability("film-line", "Video input", "The model can understand videos", includeLabels)],
            [capability == "WebSearch", createCapability("search-line", "Web search", "The model can perform web searches", includeLabels)],
            [capability == "Thinking", createCapability("lightbulb-line", "Reasoning", "The model can reason over complex tasks", includeLabels)],
            [capability == "ToolCalling", createCapability("formula", "Tool calling", "The model can call tools", includeLabels)],
            [capability == "CodeExecution", createCapability("code-s-slash-line", "Code execution", "The model can execute code", includeLabels)],
            [capability == "Fetch", createCapability("link", "Access external links", "The model can access external links", includeLabels)],
            [capability == "PersistentMemory", createCapability("brain-2-line", "Long-term memory", "The model can remember long-term information", includeLabels)],
            [capability == "JsonFunctions", createCapability("braces-line", "JSON functions", "This model is compatible with JSON functions", includeLabels)],
            [capability == "Uncensored", createCapability("eye-close-line", "Uncensored", "The model is unrestricted and uncensored", includeLabels)],
            [capability == "ModelRouter", createCapability("route-line", "Model router", "The model can route to other models", includeLabels)],
            [capability == "Diffusion", createCapability("qr-scan-2-line", "Diffusion model", "The model generates text using diffusion", includeLabels)],
        )
    );

    const renderPricingTable = (model, providerPricing = false) => getModelPricingTiers(model).length
        ? el(".model-details-pricing-table",
            { class: [providerPricing && "model-list-provider-pricing"] },
            getModelPricingTiers(model).map((pitem, index, pricing) =>
                el(".model-details-pricing-row",
                    el(".model-details-pricing-threshold",
                        el("span", Icon("dropdown-list")),
                        el("strong", cond(
                            [pitem.tokenThreshold == 0 && pricing.length == 1, "All contexts"],
                            [pitem.tokenThreshold == 0, () => el.fragment(Icon("skip-left-line"), formatTokenCount(pricing[index + 1].tokenThreshold))],
                            [true, el.fragment(Icon("arrow-right-s-line"), formatTokenCount(pitem.tokenThreshold))]
                        ))
                    ),
                    providerPricing
                        ? el.fragment(
                            el(".model-details-price-group",
                                el(".model-details-price",
                                    el("span", Icon("arrow-down-s-line"), " Input"),
                                    el("strong", formatCurrency(pitem.inputPerMillionTokens))
                                ),
                                pitem.cachedInputPerMillionTokens != null && el(".model-details-price",
                                    el("span", Icon("arrow-down-double-line"), " Cached"),
                                    el("strong", formatCurrency(pitem.cachedInputPerMillionTokens))
                                ),
                                pitem.audioInputPerMillionTokens != null && el(".model-details-price",
                                    el("span", Icon("volume-up-line"), " Audio input"),
                                    el("strong", formatCurrency(pitem.audioInputPerMillionTokens))
                                ),
                                el("small", "/mtok")
                            ),
                            el(".model-details-price-group",
                                el(".model-details-price",
                                    el("span", Icon("arrow-up-s-line"), " Output"),
                                    el("strong", formatCurrency(pitem.outputPerMillionTokens))
                                ),
                                el("small", "/mtok")
                            )
                        )
                        : el.fragment(
                            el(".model-details-price",
                                el("span", Icon("arrow-down-s-line"), " Input"),
                                el("strong", formatCurrency(pitem.inputPerMillionTokens)),
                                el("small", "/mtok")
                            ),
                            pitem.cachedInputPerMillionTokens != null && el(".model-details-price",
                                el("span", Icon("arrow-down-double-line"), " Cached"),
                                el("strong", formatCurrency(pitem.cachedInputPerMillionTokens)),
                                el("small", "/mtok")
                            ),
                            pitem.audioInputPerMillionTokens != null && el(".model-details-price",
                                el("span", Icon("volume-up-line"), " Audio input"),
                                el("strong", formatCurrency(pitem.audioInputPerMillionTokens)),
                                el("small", "/mtok")
                            ),
                            el(".model-details-price",
                                el("span", Icon("arrow-up-s-line"), " Output"),
                                el("strong", formatCurrency(pitem.outputPerMillionTokens)),
                                el("small", "/mtok")
                            )
                        )
                )
            )
        )
        : MutedText("Pricing unavailable.");

    const showModelDetails = model => displayAlert({
        title: "Model details",
        type: "model-details-alert",
        dialogSize: "large",
        message: el(".model-details-dialog",
            el(".model-details-hero",
                el("img", {
                    src: `/assets/meshes/abstract_gradient_${keyedMap(model.name)}.png`,
                    alt: ""
                }),
                {
                    style: {
                        "--provider-color": getProviderColor(model.name),
                        "--provider-color-alt": getProviderColor(model.name) + "22",
                    }
                },
                modelProviderIcon(model.name),
                el(".model-details-content",
                    el(".model-details-heading",
                        el("span.model-details-provider", modelProvider(model.name)),
                        el(".model-details-title",
                            el("h2", CopiableText(model.name.replace(/^@.*\//, ''), model.name))
                        ),
                        el("p", model.description),
                        renderModelBadges(model)
                    ),
                    el(".model-details-summary",
                        enumValue(model.intelligenceLevel) && el(".model-details-summary-item",
                            el("span", Icon("lightbulb-line"), "Intelligence"),
                            el(".model-details-level-icons",
                                el("span", enumValue(model.intelligenceLevel)),
                                repeatNode(["Lowest", "Low", "Medium", "High", "Highest"].indexOf(enumValue(model.intelligenceLevel)) + 1, Icon("lightbulb-line"))
                            ),
                        ),
                        enumValue(model.speedLevel) && el(".model-details-summary-item",
                            el("span", Icon("flashlight-line"), "Speed"),
                            el(".model-details-level-icons",
                                el("span", enumValue(model.speedLevel)),
                                repeatNode(["Slowest", "Slow", "Medium", "Fast", "Ultrafast"].indexOf(enumValue(model.speedLevel)) + 1, Icon("flashlight-line"))
                            ),
                        ),
                        model.pricing?.at(0) && el(".model-details-summary-item",
                            el("span", Icon("money-dollar-circle-line"), "Pricing"),
                            el(".data-item.model-list-provider-inline-pricing",
                                el("div",
                                    Icon("arrow-down-line"),
                                    // model.meanDiscount && el("span.discounted-before", formatCurrency(model.pricing[0].inputPerMillionTokens / (1 - model.meanDiscount))),
                                    el("span", formatCurrency(model.pricing[0].inputPerMillionTokens)),
                                    {
                                        title: "Input/million tokens"
                                    }
                                ),
                                model.pricing[0].cachedInputPerMillionTokens && el("div",
                                    Icon("arrow-down-box-line"),
                                    // model.meanDiscount && el("span.discounted-before", formatCurrency(model.pricing[0].cachedInputPerMillionTokens / (1 - model.meanDiscount))),
                                    el("span", formatCurrency(model.pricing[0].cachedInputPerMillionTokens)),
                                    {
                                        title: "Cached input/million tokens"
                                    }
                                ),
                                el("div",
                                    Icon("arrow-up-line"),
                                    // model.meanDiscount && el("span.discounted-before", formatCurrency(model.pricing[0].outputPerMillionTokens / (1 - model.meanDiscount))),
                                    el("span", formatCurrency(model.pricing[0].outputPerMillionTokens)),
                                    {
                                        title: "Output/million tokens"
                                    }
                                ),
                            ),
                        ),
                        model.modelInformation?.releaseDate && el(".model-details-summary-item",
                            el("span", Icon("calendar-line"), "Released"),
                            el("strong", formatDate(model.modelInformation.releaseDate))
                        ),
                        model.modelInformation?.parameterSize && el(".model-details-summary-item",
                            el("span", Icon("database-2-line"), "Parameters"),
                            el("strong", model.modelInformation.parameterSize)
                        ),
                        model.modelInformation?.reasoningEfforts?.length > 0 && el(".model-details-summary-item",
                            el("span", Icon("lightbulb-flash-line"), "Reasoning efforts"),
                            el("strong.model-details-badge-items", model.modelInformation.reasoningEfforts.map(r => TagBadge(r)))
                        ),
                        model.capabilities?.length && el(".model-details-summary-item",
                            el("span", Icon("pages-line"), "Capabilities"),
                            el("strong.model-details-capabilities", renderModelCapabilities(model, true))
                        ),
                        enumValue(model.stability) && el(".model-details-summary-item",
                            el("span", Icon("shield-check-line"), "Stability"),
                            el("strong", enumValue(model.stability))
                        ),
                        model.routingModel && el(".model-details-summary-item",
                            el("span", Icon("route-line"), "Routes to"),
                            el("strong", model.routingModel)
                        ),
                        model.pricing?.at(0)?.subscriptionMultiplier != null && el(".model-details-summary-item",
                            el("span", Icon("percent-line"), "Usage multiplier"),
                            el("strong", formatFloat(model.pricing[0].subscriptionMultiplier), "x")
                        ),
                        el(".model-details-summary-item",
                            el("span", Icon("server-line"), "Providers"),
                            el("strong", formatNumber((model.providers ?? []).length))
                        )
                    ),
                )
            ),
            el("section.model-details-section",
                el(".model-details-section-heading",
                    el("h3", "Providers"),
                    MutedText(`${(model.providers ?? []).filter(provider => !provider.isDisabled).length} available`)
                ),
                el(".model-list-provider-list",
                    Object.entries(Object.groupBy(model.providers ?? [], provider => enumValue(provider.group) ?? "Default"))
                        .sort(([a], [b]) => providerGroupOrder.indexOf(a) - providerGroupOrder.indexOf(b))
                        .map(([group, providers], _, groups) => el.fragment(
                            groups.length > 1 && el(".model-list-provider-group", cond(
                                [group == "Default", null],
                                [group == "Flex", el.fragment(el("b", "Non-priority"), " providers, not included on standard routing")],
                                [group == "Priority", el.fragment(el("b", "Priority"), " providers, not included on standard routing")],
                                [group == "Special", el.fragment(el("b", "Special"), " providers, not included on standard routing")],
                            )),
                            providers.map(provider =>
                                el("details.model-list-provider-info",
                                    {
                                        class: [
                                            provider.isDisabled && "disabled",
                                            provider.discount && "discounted"
                                        ],
                                        open: provider == ((model.providers ?? []).find(item => !item.isDisabled) ?? model.providers[0])
                                    },
                                    el("summary",
                                        Icon("arrow-right-s-line", { classList: "model-list-provider-chevron" }),
                                        modelProviderIcon(model.name, provider.tag),
                                        el("strong",
                                            provider.name,
                                            provider.technicalInfo && provider.technicalInfo.dataCollection == "Zero" && TagIconBadge(Icon("shield-line"), " ZDR", "var(--info)"),
                                            provider.discount && TagIconBadge(Icon("coupon-2-line"), " " + (provider.discount * 100) + "% OFF", "var(--success)"),
                                            provider.isDisabled && TagIconBadge(Icon("alert-line"), " Unavailable", "var(--warn)"),
                                        ),
                                        el(".provider-data",
                                            provider.technicalInfo && provider.technicalInfo.uptimePercentage && el(".data-item",
                                                cond(
                                                    [provider.technicalInfo.uptimePercentage >= 98, el("span", Icon("signal-cellular-3-fill"), " " + formatFloat(provider.technicalInfo.uptimePercentage) + "%", { style: { color: "var(--success)" } })],
                                                    [provider.technicalInfo.uptimePercentage >= 85, el("span", Icon("signal-cellular-2-fill"), " " + formatFloat(provider.technicalInfo.uptimePercentage) + "%", { style: { color: "var(--warn)" } })],
                                                    [true, el("span", Icon("signal-cellular-1-fill"), " " + formatFloat(provider.technicalInfo.uptimePercentage) + "%", { style: { color: "var(--warn)" } })],
                                                ),
                                                {
                                                    title: "Uptime (15 min)"
                                                }
                                            ) || el(".data-item"),
                                            provider.technicalInfo && provider.technicalInfo.throughputTokensPerSecond && el(".data-item",
                                                el("span", provider.technicalInfo.throughputTokensPerSecond, MutedText("/tps"), {
                                                    title: "Tokens per second (15 min)"
                                                }),
                                                provider.technicalInfo.throughputTokensPerSecond < 10 && Icon("alert-line", { style: { color: "var(--warn)" } })
                                            ) || el(".data-item"),
                                            provider.technicalInfo && provider.technicalInfo.latencySeconds && el(".data-item",
                                                el("span", formatFlexibibleFloat(provider.technicalInfo.latencySeconds), MutedText("s"), {
                                                    title: "Latency (15 min)"
                                                }),
                                                provider.technicalInfo.latencySeconds > 5 && Icon("alert-line", { style: { color: "var(--warn)" } })
                                            ) || el(".data-item"),
                                            provider.pricing?.at(0) && el(".data-item.model-list-provider-inline-pricing",
                                                el("div",
                                                    Icon("arrow-down-line"),
                                                    provider.discount && el("span.discounted-before", formatCurrency(provider.pricing[0].inputPerMillionTokens / (1 - provider.discount))),
                                                    el("span", formatCurrency(provider.pricing[0].inputPerMillionTokens)),
                                                    {
                                                        title: "Input/million tokens"
                                                    }
                                                ),
                                                provider.pricing[0].cachedInputPerMillionTokens && el("div",
                                                    Icon("arrow-down-box-line"),
                                                    provider.discount && el("span.discounted-before", formatCurrency(provider.pricing[0].cachedInputPerMillionTokens / (1 - provider.discount))),
                                                    el("span", formatCurrency(provider.pricing[0].cachedInputPerMillionTokens)),
                                                    {
                                                        title: "Cached input/million tokens"
                                                    }
                                                ),
                                                el("div",
                                                    Icon("arrow-up-line"),
                                                    provider.discount && el("span.discounted-before", formatCurrency(provider.pricing[0].outputPerMillionTokens / (1 - provider.discount))),
                                                    el("span", formatCurrency(provider.pricing[0].outputPerMillionTokens)),
                                                    {
                                                        title: "Output/million tokens"
                                                    }
                                                ),
                                            ),
                                        )
                                    ),
                                    el(".model-list-provider-content",
                                        el("h4", "Provider information"),
                                        provider.routingTag && el(".model-list-provider-technical.model-list-provider-tag",
                                            el(".model-list-provider-field",
                                                el("strong", "Provider tag"),
                                                el("span", CopiableText(provider.routingTag))
                                            )
                                        ),
                                        provider.technicalInfo && Object.values(provider.technicalInfo).some(value => value != null)
                                            ? el(".model-list-provider-technical",
                                                provider.technicalInfo.contextWindow != null && el(".model-list-provider-field",
                                                    el("strong", "Context window"),
                                                    el("span", ShortSize(provider.technicalInfo.contextWindow), " tokens", {
                                                        title: formatNumber(provider.technicalInfo.contextWindow) + " tokens"
                                                    })
                                                ),
                                                provider.technicalInfo.maxOutputTokens != null && el(".model-list-provider-field",
                                                    el("strong", "Maximum output"),
                                                    el("span", ShortSize(provider.technicalInfo.maxOutputTokens), " tokens", {
                                                        title: formatNumber(provider.technicalInfo.maxOutputTokens) + " tokens"
                                                    })
                                                ),
                                                provider.technicalInfo.throughputTokensPerSecond != null && el(".model-list-provider-field",
                                                    el("strong", "Throughput"),
                                                    el("span", formatNumber(provider.technicalInfo.throughputTokensPerSecond), " tokens/s")
                                                ),
                                                provider.technicalInfo.latencySeconds != null && el(".model-list-provider-field",
                                                    el("strong", "Latency"),
                                                    el("span", formatNumber(provider.technicalInfo.latencySeconds), " s")
                                                ),
                                                provider.technicalInfo.uptimePercentage != null && el(".model-list-provider-field",
                                                    el("strong", "Uptime"),
                                                    el("span", formatFloat(provider.technicalInfo.uptimePercentage), "%")
                                                ),
                                                enumValue(provider.technicalInfo.dataCollection) != null && el(".model-list-provider-field",
                                                    el("strong", "Data collection"),
                                                    el("span", cond(
                                                        [provider.technicalInfo.dataCollection == "Zero", TagIconBadge(Icon("shield-line"), " Zero data retention", "var(--success)")],
                                                        [provider.technicalInfo.dataCollection == "PromptNoTraining", TagIconBadge(Icon("alert-line"), " Prompts, no training", "var(--warn)")],
                                                        [provider.technicalInfo.dataCollection == "PromptWithTraining", TagIconBadge(Icon("alert-line"), " Prompts with training", "var(--warn)")],
                                                        [provider.technicalInfo.dataCollection == "PromptWithPublishing", TagIconBadge(Icon("alert-line"), " Prompts with traning, may publish prompts", "var(--warn)")],
                                                        [true, TagIconBadge(Icon("alert-line"), " " + enumValue(provider.technicalInfo.dataCollection), "var(--warn)")]
                                                    ))
                                                ),
                                                provider.isStealthProvider && el(".model-list-provider-field",
                                                    el("strong", "Origin"),
                                                    el("span", TagIconBadge(Icon("alert-line"), " Stealth", "var(--warn)", {
                                                        title: "This particular provider offers this model through a special, undocumented, or non-standard deal."
                                                    }))
                                                ),
                                                provider.technicalInfo.quantization && el(".model-list-provider-field",
                                                    el("strong", "Quantization"),
                                                    el("span", provider.technicalInfo.quantization.toUpperCase())
                                                ),
                                                provider.technicalInfo.deploymentRegion && el(".model-list-provider-field",
                                                    el("strong", "Deployment region"),
                                                    el("span", provider.technicalInfo.deploymentRegion)
                                                )
                                            )
                                            : MutedText("Technical information unavailable."),
                                        el("h4", "Pricing"),
                                        renderPricingTable(provider, true)
                                    )
                                )
                            )
                        )),
                    !(model.providers ?? []).length && MutedText("No providers available.")
                )
            )
        )
    });

    return Dashboard(
        el.state.listening([models], () => DashboardTitle("Model gateway", `${models.value?.length ?? "-"} models`)),
        TabControl([
            {
                title: "Text models",
                icon: Icon("text-block"),
                contents: Content(
                    Section(
                        CommandBar(
                            FormControl("Search models",
                                TextInput("searchInput", "Search for model", {
                                    value: focusedModel,
                                    onInput(event) {
                                        throttledCall(() => {
                                            filterName.value = event.target.value.trim();
                                            resetVisibleModels();
                                        }, 300);
                                    },
                                    type: 'search'
                                })
                            ),
                            FormControl("Speed",
                                Select(
                                    {
                                        value: null,
                                        items: [
                                            {
                                                value: "",
                                                text: "All speeds"
                                            },
                                            {
                                                value: "Ultrafast",
                                                text: "Ultra fast"
                                            },
                                            {
                                                value: "Fast"
                                            },
                                            {
                                                value: "Medium"
                                            },
                                            {
                                                value: "Slow"
                                            },
                                            {
                                                value: "Slowest"
                                            },
                                        ],
                                        onChange(event) {
                                            filterSpeed.value = event.target.value;
                                            resetVisibleModels();
                                        }
                                    }
                                )
                            ),
                            FormControl("Intelligence",
                                Select(
                                    {
                                        value: null,
                                        items: [
                                            {
                                                value: "",
                                                text: "All intelligences"
                                            },
                                            {
                                                value: "Highest"
                                            },
                                            {
                                                value: "High"
                                            },
                                            {
                                                value: "Medium"
                                            },
                                            {
                                                value: "Low"
                                            },
                                            {
                                                value: "Lowest"
                                            },
                                        ],
                                        onChange(event) {
                                            filterIntelligence.value = event.target.value;
                                            resetVisibleModels();
                                        }
                                    }
                                )
                            ),
                            FormControl("Price",
                                Select(
                                    {
                                        value: null,
                                        items: [
                                            {
                                                value: "",
                                                text: "All prices"
                                            },
                                            {
                                                value: "cheapest",
                                                text: "Cheapest"
                                            },
                                            {
                                                value: "cheap",
                                                text: "Cheap"
                                            },
                                            {
                                                value: "midrange",
                                                text: "Mid range"
                                            },
                                            {
                                                value: "costly",
                                                text: "Costly"
                                            },
                                            {
                                                value: "expensive",
                                                text: "Expensive"
                                            },
                                        ],
                                        onChange(event) {
                                            filterPricing.value = event.target.value;
                                            resetVisibleModels();
                                        }
                                    }
                                )
                            ),
                            // FormControl("Subscription",
                            //     Select(
                            //         {
                            //             value: null,
                            //             items: [
                            //                 {
                            //                     value: "",
                            //                     text: "All models"
                            //                 },
                            //                 {
                            //                     value: "Included"
                            //                 }
                            //             ],
                            //             onChange(event) {
                            //                 filterSubscription.value = event.target.value;
                            //                 resetVisibleModels();
                            //             }
                            //         }
                            //     )
                            // ),
                            FormControl("Release date",
                                Select(
                                    {
                                        value: null,
                                        items: [
                                            {
                                                value: "",
                                                text: "All release dates"
                                            },
                                            {
                                                value: "thisweek",
                                                text: "This week"
                                            },
                                            {
                                                value: "thismonth",
                                                text: "This month"
                                            },
                                            {
                                                value: "threemonths",
                                                text: "3 months"
                                            },
                                            {
                                                value: "thisyear",
                                                text: "This year"
                                            },
                                        ],
                                        onChange(event) {
                                            filterReleaseDate.value = event.target.value;
                                            resetVisibleModels();
                                        }
                                    }
                                )
                            ),
                            FormControl("Capability",
                                MultiSelect(
                                    {
                                        value: [],
                                        items: [
                                            {
                                                label: "All capabilities",
                                                value: ""
                                            },
                                            {
                                                label: "Image",
                                                value: "ImageInput"
                                            },
                                            {
                                                label: "Video",
                                                value: "VideoInput"
                                            },
                                            {
                                                label: "Audio",
                                                value: "AudioInput"
                                            },
                                            {
                                                label: "Files",
                                                value: "FileInput"
                                            },
                                            {
                                                label: "Thinking",
                                                value: "Thinking"
                                            }
                                        ],
                                        onChange(selected) {
                                            filterCapability.value = [...selected];
                                            resetVisibleModels();
                                        }
                                    }
                                )
                            ),
                        )
                    ),
                    el.state.listening([models, visibleModelCount, filterName, filterIntelligence, filterPricing, filterSpeed, filterReleaseDate, filterCapability], () =>
                        Section(
                            Card({ classList: "bg-card" },
                                CardHeader("Available models"),
                                CardContent(
                                    models.value && pipe(filteredModels(), models =>
                                        el(".model-list-view",
                                            models.slice(0, visibleModelCount.value).map(model =>
                                                el(".model-item",
                                                    {
                                                        class: [model.flags.isDeprecating && "deprecated"],
                                                        tabIndex: model.name === focusedModel ? -1 : null,
                                                        mount() {
                                                            if (model.name !== focusedModel) {
                                                                return;
                                                            }

                                                            requestAnimationFrame(() => {
                                                                this.focus({ preventScroll: true });
                                                                this.scrollIntoView({ behavior: "smooth", block: "center" });
                                                            });
                                                        }
                                                    },
                                                    ModelBanner(model.name),
                                                    el(".model-data",
                                                        el("h2",
                                                            modelProviderIcon(model.name),
                                                            el(".model-details-link",
                                                                MutedText(model.name.replace(/\/.*/, '') + "/"),
                                                                model.name.replace(/^@.*\//, ''),
                                                                Icon("arrow-right-up-line"),
                                                                {
                                                                    type: "button",
                                                                    onClick() {
                                                                        showModelDetails(model);
                                                                    }
                                                                }
                                                            )
                                                        ),
                                                        renderModelBadges(model),
                                                        el("p", model.description),
                                                        el(".model-info",
                                                            el(".model-info-item.clickable",
                                                                "by ", el("strong", modelProvider(model.name), { style: { fontWeight: 500 } }),
                                                                {
                                                                    onClick() {
                                                                        find("searchInput").value = '@' + modelProvider(model.name);
                                                                        find("searchInput").dispatchEvent(new Event("input", {
                                                                            bubbles: true,
                                                                            cancelable: true
                                                                        }));
                                                                        scrollTo(0, 0);
                                                                    }
                                                                }
                                                            ),
                                                            model.modelInformation?.releaseDate && el(".model-info-item",
                                                                "released ", formatDate(model.modelInformation.releaseDate)
                                                            ),
                                                            model.modelInformation?.parameterSize && el(".model-info-item",
                                                                model.modelInformation.parameterSize, " parameters"
                                                            ),
                                                            model.routingModel && el(".model-info-item",
                                                                Icon("corner-up-right-double-fill"), " ", model.routingModel,
                                                                {
                                                                    title: "Currently pointing to " + model.routingModel
                                                                }
                                                            ),
                                                            el(".model-info-item",
                                                                cond(
                                                                    [enumValue(model.stability) == "Stable", Content(Icon("signal-cellular-3-fill"), " Stable", { style: { color: "var(--success)" } })],
                                                                    [enumValue(model.stability) == "Unstable", Content(Icon("signal-cellular-2-fill"), " Unstable", { style: { color: "var(--warn)" } })],
                                                                    [enumValue(model.stability) == "Offline", Content(Icon("signal-cellular-1-fill"), " Offline", { style: { color: "var(--warn)" } })],
                                                                )
                                                            ),
                                                        ),
                                                        pipe(getModelPricingTiers(model), pricing =>
                                                            el(".model-info-item-table",
                                                                pricing.map((pitem, index) =>
                                                                    el(".model-info-item-row",
                                                                        el(".model-info-item-col",
                                                                            el(".definition", { title: "From context window" },
                                                                                el("span", Icon("dropdown-list")),
                                                                                el("span", cond(
                                                                                    [pitem.tokenThreshold == 0 && pricing.length == 1, "All contexts"],
                                                                                    [pitem.tokenThreshold == 0, () => el.fragment(Icon("skip-left-line"), formatTokenCount(pricing[index + 1].tokenThreshold))],
                                                                                    [true, el.fragment(Icon("arrow-right-s-line"), formatTokenCount(pitem.tokenThreshold))]
                                                                                ))
                                                                            )
                                                                        ),

                                                                        el(".model-info-item-col",
                                                                            {
                                                                                style: {
                                                                                    "--col-width": "70px"
                                                                                }
                                                                            },
                                                                            el(".definition",
                                                                                el("span", Icon("arrow-down-s-line"), " Input"),
                                                                                el("span", formatCurrency(pitem.inputPerMillionTokens))
                                                                            ),
                                                                            pitem.cachedInputPerMillionTokens && el(".definition",
                                                                                el("span", Icon("arrow-down-double-line"), " Cached"),
                                                                                el("span", formatCurrency(pitem.cachedInputPerMillionTokens))
                                                                            ),
                                                                            pitem.audioInputPerMillionTokens && el(".definition",
                                                                                el("span", Icon("volume-up-line"), " Audio in."),
                                                                                el("span", formatCurrency(pitem.audioInputPerMillionTokens))
                                                                            ),
                                                                            el(".definition", { style: { opacity: .65 } },
                                                                                el("span"),
                                                                                el("span", "/mtok")
                                                                            ),
                                                                        ),
                                                                        el(".model-info-item-col",
                                                                            {
                                                                                style: {
                                                                                    "--col-width": "70px"
                                                                                }
                                                                            },
                                                                            el(".definition",
                                                                                el("span", Icon("arrow-up-s-line"), "Output"),
                                                                                el("span", formatCurrency(pitem.outputPerMillionTokens))
                                                                            ),
                                                                            el(".definition", { style: { opacity: .65 } },
                                                                                el("span"),
                                                                                el("span", "/mtok")
                                                                            ),
                                                                        ),
                                                                    ))
                                                            )
                                                        )
                                                    ),
                                                    el(".model-meta",
                                                        model.pricing?.at(0)?.subscriptionMultiplier != null && el(".model-meta-item",
                                                            el("strong", "Usage multiplier"),
                                                            el(".model-meta-item-number-label", formatFloat(model.pricing[0].subscriptionMultiplier), "x"),
                                                            {
                                                                title: `This model has included usage for AIVAX subscription plans and it's measured at ${formatNumber(model.pricing[0].subscriptionMultiplier)}x rate for all context lengths.`
                                                            }
                                                        ),

                                                        model.capabilities?.length && el(".model-meta-item",
                                                            el("strong", "Capabilities"),
                                                            el(".iconic-information", renderModelCapabilities(model, true))
                                                        )
                                                    ),
                                                    el(".model-actions",
                                                        Dropdown(null,
                                                            DropdownItem("Copy model name", Icon("file-copy-line"), {
                                                                onClick() {
                                                                    copyToClipboard(model.name)
                                                                }
                                                            }),
                                                            DropdownDivider(),
                                                            DropdownItem("Create gateway", Icon("route-line"), {
                                                                onClick() {
                                                                    displayFormControl({
                                                                        title: "Create model gateway",
                                                                        message: `Create a new model gateway using the ${model.name} model. This will allow you to use this model in your conversations and RAG collections.`,
                                                                        dialogSize: "small",
                                                                        formControlItem: SpecializedControl({
                                                                            fields: [
                                                                                {
                                                                                    name: "gatewayName",
                                                                                    label: "Gateway name",
                                                                                    control: TextInput(null, "My gateway")
                                                                                }
                                                                            ]
                                                                        }),
                                                                        onConfirm(data) {
                                                                            runOperation("Creating gateway...", async () => {
                                                                                var gateway = await api.postJson("/api/v1/ai-gateways", {
                                                                                    name: data.gatewayName,
                                                                                    parameters: {
                                                                                        modelName: model.name,
                                                                                        baseAddress: "@integrated"
                                                                                    }
                                                                                });

                                                                                page(`/dashboard/gateways/${gateway.data.aiGatewayId}`);
                                                                            });
                                                                        }
                                                                    })
                                                                }
                                                            }),
                                                            DropdownDivider(),
                                                            DropdownItem("Open playground", Icon("chat-1-line"), {
                                                                onClick() {
                                                                    const params = new URLSearchParams({
                                                                        "api-endpoint": window.env.apiEndpoint + "v1/chat/completions",
                                                                        "api-model": model.name,
                                                                        "api-key": getSession()?.sessionKey
                                                                    });
                                                                    const playgroundUrl = `https://playground.aivax.net/?${params.toString()}`;
                                                                    window.open(playgroundUrl, "_blank");
                                                                }
                                                            }),
                                                            DropdownItem("View integration code", Icon("braces-line"), {
                                                                onClick() {
                                                                    displayAlert({
                                                                        title: "Model code",
                                                                        dialogSize: "medium",
                                                                        message: el.fragment(
                                                                            "Use the code below to integrate your model.",
                                                                            MultilingualCodeWrapper("Example", [
                                                                                {
                                                                                    label: "Python",
                                                                                    language: "python",
                                                                                    code: `
import os
from openai import OpenAI

client = OpenAI(
    base_url="https://inference.aivax.net/api/v1",
    api_key=os.environ["AIVAX_API_KEY"],
)

completion = client.chat.completions.create(
    model="${model.name}",
    messages=[
        {
            "role": "user",
            "content": "What is the capital of France?"
        }
    ],
)

print(completion.choices[0].message)`
                                                                                },
                                                                                {
                                                                                    label: "JavaScript",
                                                                                    language: "javascript",
                                                                                    code: `
import { OpenAI } from "openai";

const client = new OpenAI({
baseURL: "https://inference.aivax.net/api/v1",
apiKey: process.env.AIVAX_API_KEY,
});

const chatCompletion = await client.chat.completions.create({
    model: "${model.name}",
    messages: [
        {
            role: "user",
            content: "What is the capital of France?",
        },
    ],
});

console.log(chatCompletion.choices[0].message);`
                                                                                },
                                                                                {
                                                                                    label: "Curl",
                                                                                    language: "bash",
                                                                                    code: `
curl https://inference.aivax.net/api/v1/chat/completions \\
    -H "Authorization: Bearer $AIVAX_API_KEY" \\
    -H 'Content-Type: application/json' \\
    -d '{
        "messages": [
            {
                "role": "user",
                "content": "What is the capital of France?"
            }
        ],
        "model": "${model.name}",
        "stream": false
    }'
                                                                                                                                                 `
                                                                                }
                                                                            ])
                                                                        )
                                                                    })
                                                                }
                                                            }),
                                                            DropdownItem("View model details", Icon("barcode-line"), {
                                                                onClick() {
                                                                    showModelDetails(model);
                                                                }
                                                            }),
                                                        )
                                                    )
                                                )
                                            )?.nullWhenEmpty() || el(".no-models", "No models found matching your criteria."),
                                            visibleModelCount.value < models.length && LoadMoreSentinel(models.length)
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            },
            {
                title: "Image models",
                icon: Icon("image-line"),
                contents: CatalogList(imageModels, "Image generation models", "image-models.json")
            },
            {
                title: "Speech-to-text models",
                icon: Icon("text-to-speech-line"),
                contents: CatalogList(transcriptionModels, "Speech-to-text models", "audio-transcription-models.json")
            },
            {
                title: "Text-to-speech models",
                icon: Icon("speak-line"),
                contents: CatalogList(speechModels, "Text-to-speech models", "speech-models.json")
            },
            {
                title: "Reranking models",
                icon: Icon("sort-desc"),
                contents: CatalogList(rerankingModels, "Reranking models", "rerankers-models.json")
            },
            {
                title: "Decision models",
                icon: Icon("brain-2-line"),
                contents: CatalogList(decisionModels, "Decision models", "decisions-models.json")
            },
        ])
    )
}
